import { useTranslation } from 'react-i18next';
import { useDialog } from './DialogProvider';
import type { ConfirmDialogOptions } from './dialogTypes';

/**
 * Enhanced hook with helper methods for common dialog types
 */
export function useDialogHelpers() {
  const { t } = useTranslation();
  const { showDialog, hideDialog, hideAllDialogs } = useDialog();

  /**
   * Show a confirmation dialog
   */
  const confirm = (options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise(resolve => {
      showDialog({
        ...options,
        type: 'confirm',
        onConfirm: async () => {
          if (options.onConfirm) {
            await options.onConfirm();
          }
          resolve(true);
        },
        onCancel: () => {
          if (options.onCancel) {
            options.onCancel();
          }
          resolve(false);
        },
      });
    });
  };

  /**
   * Show an alert dialog
   */
  const alert = (title: string, message?: string): Promise<void> => {
    return new Promise(resolve => {
      showDialog({
        title,
        message,
        type: 'alert',
        onConfirm: () => resolve(),
      });
    });
  };

  /**
   * Show a delete confirmation dialog
   */
  const confirmDelete = (itemName?: string): Promise<boolean> => {
    return confirm({
      title: itemName || 'Confirm Delete',
      confirmText: t('delete_confirm_modal'),
      cancelText: t('cancel_confirm_modal'),
    });
  };

  /**
   * Show a custom dialog with a React component
   */
  const showCustomDialog = (
    title: string,
    CustomComponent: React.ComponentType<{ onClose: () => void }>
  ): string => {
    return showDialog({
      title,
      type: 'custom',
      customComponent: CustomComponent,
    });
  };

  return {
    // Core dialog methods
    showDialog,
    hideDialog,
    hideAllDialogs,

    // Helper methods
    confirm,
    alert,
    confirmDelete,
    showCustomDialog,
  };
}
